import Storage from './LocalStorage';

export enum Level {
	DEBUG = 0,
	INFO = 1,
	WARN = 2,
	ERROR = 3,
	FATAL = 4,
}

class LogFile {

	private static LOG_KEY = 'log';
	private static PURGE_FACTOR = 0.2;

	private firstIndex: number;
	private nextIndex: number;
	private maxLines = 1000;

	constructor() {
		const jsonIndexes = Storage.getItem(LogFile.LOG_KEY);
		const indexes = jsonIndexes ? JSON.parse(jsonIndexes) : {
			firstIndex: 0,
			nextIndex: 0
		};

		this.firstIndex = indexes.firstIndex;
		this.nextIndex = indexes.nextIndex;

		if (!jsonIndexes) {
			this.saveIndexes();
		}
	}

	private getLineKey(index: number) {
		return `log-entry-${index}`;
	}

	private saveIndexes() {
		Storage.setItem(LogFile.LOG_KEY, JSON.stringify({
			firstIndex: this.firstIndex,
			nextIndex: this.nextIndex
		}));
	}

	private removeOldEntries() {
		if (this.nextIndex - this.firstIndex <= this.maxLines)
			return;

		const lastIndexToRemove = this.firstIndex + LogFile.PURGE_FACTOR * this.maxLines;
		for (let i = this.firstIndex; i < lastIndexToRemove; i++) {
			Storage.removeItem(this.getLineKey(i));
		}

		this.firstIndex = lastIndexToRemove;
		this.saveIndexes();
	}

	public write(line: string) {
		Storage.setItem(this.getLineKey(this.nextIndex), line);

		this.nextIndex++;
		this.saveIndexes();

		this.removeOldEntries();
	}

	public getLines(): string[] {
		const entries = new Array<string>();
		for (let i = this.firstIndex; i < this.nextIndex; i++)
			entries.push(Storage.getItem(this.getLineKey(i)) || '');
		return entries;
	}

	public removeAllLines() {
		for (let i = this.firstIndex; i < this.nextIndex; i++)
			Storage.removeItem(this.getLineKey(i));

		this.firstIndex = this.nextIndex = 0;
		this.saveIndexes();
	}

	public setMaxLines(maxLines: number) {
		this.maxLines = maxLines;
	}
}

export interface IAppender {
	append(date: Date, level: Level, message: string): void
}

class Logger {


	private file: LogFile;
	private currentLevel: Level;
	private consoleEnabled: boolean;
	private appender: IAppender | undefined;

	constructor() {
		this.currentLevel = Level.INFO;
		this.consoleEnabled = false;
		this.file = new LogFile();

	}

	public init(level: Level, appender?: IAppender) {
		this.currentLevel = level;
		this.appender = appender;
	}

	private getTimestamp(date: Date): string {
		const pad = (x: number) => x < 10 ? '0' + x : x.toString();
		return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())},${date.getMilliseconds()}`;
	}

	private write(level: Level, message: string) {

		if (level < this.currentLevel)
			return;

		const date = new Date();
		const levelString = (Level[level] + '     ').substr(0, 5);
		const entry = `${this.getTimestamp(date)} : ${levelString} : ${message}`;

		if (this.consoleEnabled)
			// eslint-disable-next-line no-console
			console.log(entry);

		this.file.write(entry);

		this.appender?.append(date, level, message);
	}

	public debug(msg: string) {
		this.write(Level.DEBUG, msg);
	}

	public info(msg: string) {
		this.write(Level.INFO, msg);
	}

	public warn(msg: string) {
		this.write(Level.WARN, msg);
	}

	public error(msg: string) {
		this.write(Level.ERROR, msg);
	}

	public fatal(msg: string) {
		this.write(Level.FATAL, msg);
	}

	public setLevel(level: Level) {
		this.currentLevel = level;
	}

	public setMaxEntries(max: number) {
		this.file.setMaxLines(max);
	}

	public enableConsole(value = true) {
		this.consoleEnabled = value;
	}

	public getEntries(): string[] {
		return this.file!.getLines();
	}

	public clearAllEntries() {
		this.file.removeAllLines();
	}
}

export const Log = new Logger();
